import React, { useEffect, useState } from 'react';
import './ManualFeeInvoicePopup.scss';
import { ModalBody } from 'reactstrap';
import moment from 'moment';
import Loader from 'react-loaders';
import Rodal from '../../Rodal/Rodal';
import ButtonLoader from '../../Layout/Buttons/ButtonLoader';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import Helpers from '../../../utils/helper';
import { ManualFeeInvoiceDocument } from '../../../models/EngagementHistory/ManualFeeInvoiceDocument';

export interface ManualFeeInvoicePopupProps {
    isVisible: boolean;
    onClose: () => void;
    engagementId: number;
    historyRecordId?: number;
}

const ManualFeeInvoicePopupWidth = 700;
const ManualFeeInvoiceDateFormat = 'MMMM D, YYYY';
const ManualFeeInvoiceLoadError = 'Could not load the invoice.';
const ManualFeeInvoiceDownloadError = 'Could not download the invoice.';
const ManualFeeInvoiceLineItemQuantity = '1';
const ManualFeeInvoiceLineItemName = 'Monthly Retainer Fee';

export const ManualFeeInvoicePopup = (props: ManualFeeInvoicePopupProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [invoice, setInvoice] = useState<ManualFeeInvoiceDocument>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if (props.isVisible && props.historyRecordId != null) {
            fetchInvoice();
        }

        if (!props.isVisible) {
            setInvoice(null);
        }
    }, [props.isVisible, props.historyRecordId]);

    const fetchInvoice = () => {
        setIsLoading(true);
        axios
            .get(
                `/api/Marketplace/${props.engagementId}/EngagementHistory/manual-fees/${props.historyRecordId}/invoice`
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(ManualFeeInvoiceLoadError, 'error');
                    props.onClose();
                    return;
                }

                setInvoice(response.data as ManualFeeInvoiceDocument);
            })
            .catch(() => {
                createNotification(ManualFeeInvoiceLoadError, 'error');
                props.onClose();
            })
            .finally(() => setIsLoading(false));
    };

    const downloadInvoicePdf = () => {
        setIsDownloading(true);
        axios
            .get(
                `/api/Marketplace/${props.engagementId}/EngagementHistory/manual-fees/${props.historyRecordId}/invoice/pdf`,
                { responseType: 'blob' }
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(ManualFeeInvoiceDownloadError, 'error');
                    return;
                }

                const fileName = invoice?.invoiceNumber
                    ? `${invoice.invoiceNumber}.pdf`
                    : `invoice-${props.historyRecordId}.pdf`;

                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: 'application/pdf' })
                );

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(() => {
                createNotification(ManualFeeInvoiceDownloadError, 'error');
            })
            .finally(() => setIsDownloading(false));
    };

    const renderLoader = () => (
        <div className="manual-fee-invoice-loader">
            <Loader active={isLoading} type="ball-scale-multiple" />
        </div>
    );

    const renderHeader = () => (
        <div className="manual-fee-invoice-header">
            <div className="manual-fee-invoice-header__company bold">
                {invoice.companyName}
            </div>
            <div className="manual-fee-invoice-header__title bold">Invoice</div>
        </div>
    );

    const renderSummary = () => (
        <div className="manual-fee-invoice-summary">
            <div className="manual-fee-invoice-summary__client">
                <div>{invoice.clientName}</div>
                <div>{invoice.practiceName}</div>
                <div>{invoice.clientEmail}</div>
            </div>
            <div className="manual-fee-invoice-summary__meta">
                <div className="manual-fee-invoice-summary__meta-row">
                    <span className="manual-fee-invoice-summary__meta-label">
                        Invoice #:
                    </span>
                    <span className="manual-fee-invoice-summary__meta-value">
                        {invoice.invoiceNumber}
                    </span>
                </div>
                <div className="manual-fee-invoice-summary__meta-row">
                    <span className="manual-fee-invoice-summary__meta-label">
                        Invoice Date:
                    </span>
                    <span className="manual-fee-invoice-summary__meta-value">
                        {moment(invoice.invoiceDate).format(
                            ManualFeeInvoiceDateFormat
                        )}
                    </span>
                </div>
                <div className="manual-fee-invoice-summary__meta-row">
                    <span className="manual-fee-invoice-summary__meta-label">
                        Payment Date:
                    </span>
                    <span className="manual-fee-invoice-summary__meta-value">
                        {invoice.paymentDate
                            ? moment(invoice.paymentDate).format(
                                  ManualFeeInvoiceDateFormat
                              )
                            : ''}
                    </span>
                </div>
            </div>
        </div>
    );

    const renderLineItemsTable = () => (
        <table className="manual-fee-invoice-table">
            <tbody>
                <tr>
                    <td className="manual-fee-invoice-table__name bold">
                        {ManualFeeInvoiceLineItemName}
                    </td>
                    <td className="manual-fee-invoice-table__description">
                        {`Month ${invoice.monthNumber} Retainer Fee for advisory services`}
                    </td>
                    <td className="manual-fee-invoice-table__quantity">
                        {ManualFeeInvoiceLineItemQuantity}
                    </td>
                    <td className="manual-fee-invoice-table__amount">
                        {Helpers.GetCurrencyFormat(invoice.amountDue)}
                    </td>
                </tr>
                <tr>
                    <td
                        colSpan={3}
                        className="manual-fee-invoice-table__total-label bold">
                        Total Due
                    </td>
                    <td className="manual-fee-invoice-table__amount bold">
                        {Helpers.GetCurrencyFormat(invoice.amountDue)}
                    </td>
                </tr>
            </tbody>
        </table>
    );

    const renderRemitTo = () => (
        <div className="manual-fee-invoice-remit">
            <span className="manual-fee-invoice-remit__label bold">
                Please remit payment to:
            </span>
            <span className="manual-fee-invoice-remit__lines">
                {invoice.remitToLines.map((line, index) => (
                    <span key={`remit-line-${index}`}>{line}</span>
                ))}
            </span>
        </div>
    );

    const renderFooter = () => (
        <div className="manual-fee-invoice-footer">
            {invoice.footerLines.map((line, index) => (
                <div
                    className="manual-fee-invoice-footer-line"
                    key={`footer-line-${index}`}>
                    {line}
                </div>
            ))}
        </div>
    );

    const renderInvoice = () => (
        <div className="invoice-sheet">
            {renderHeader()}
            {renderSummary()}
            {renderLineItemsTable()}
            {renderRemitTo()}
            <div className="manual-fee-invoice-thanks">Thank you.</div>
            {renderFooter()}
        </div>
    );

    const renderButtons = () => (
        <div className="manual-fee-invoice-buttons">
            <button className="btn btn-secondary" onClick={props.onClose}>
                Close
            </button>
            <ButtonLoader
                type="submit"
                className="btn btn-primary"
                disabled={isDownloading}
                isLoading={isDownloading}
                loaderButtonText=""
                buttonText="Print"
                onClick={downloadInvoicePdf}
            />
        </div>
    );

    return (
        <Rodal
            visible={props.isVisible}
            onClose={props.onClose}
            animation="slideUp"
            className="manual-fee-invoice-popup"
            showMask={true}
            width={ManualFeeInvoicePopupWidth}
            showCloseButton={true}>
            <ModalBody>
                {isLoading || !invoice ? renderLoader() : renderInvoice()}
                {!isLoading && invoice && renderButtons()}
            </ModalBody>
        </Rodal>
    );
};

export default ManualFeeInvoicePopup;
